import {map, takeUntil, mergeMap} from 'rxjs/operators';
import { Component,
  OnInit,
  AfterViewInit,
  Input,
  Output,
  HostListener,
  ElementRef,
  ChangeDetectorRef,
  EventEmitter,
  ViewChild } from '@angular/core';
import { TooltipService } from '../../services/tooltip.service';
import { AnalyticsService } from '../../services/analytics.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-tooltip-content',
  templateUrl: './tooltip-content.component.html',
  styleUrls: ['./tooltip-content.component.css']
})
export class TooltipContentComponent implements OnInit, AfterViewInit {
  @ViewChild('tooltipcontent') tooltipcontent;
	@Input() tooltipTitle: string;
  @Input() tooltipContent: string;
  @Input() helpLink: string;
  @Input() tooltipRef: any;
  @Output() close = new EventEmitter();
  mouseup = new EventEmitter<MouseEvent>();
  mousedown = new EventEmitter<MouseEvent>();
  mousemove = new EventEmitter<MouseEvent>();

  mousedrag: Observable<{top, left}>;

  top: number;
  left: number;

  underHostMode: {
    active: boolean,
    arrowLeftPosition: number;
  } = {
    active: false,
    arrowLeftPosition: 3
  };

  @HostListener('document:mouseup', ['$event'])
  onMouseup(event: MouseEvent) {
    this.mouseup.emit(event);
  }

  @HostListener('mousedown', ['$event'])
  onMousedown(event: MouseEvent) {
    this.mousedown.emit(event);
    return false; // Call preventDefault() on the event
  }

  @HostListener('document:mousemove', ['$event'])
  onMousemove(event: MouseEvent) {
    this.mousemove.emit(event);
  }

  constructor(private element: ElementRef,
              private cdr: ChangeDetectorRef,
              private tooltipSvc: TooltipService,
              private analyticsSvc: AnalyticsService) {
    this.mousedrag = this.mousedown.pipe(map(event => {
      return {
        top: event.clientY - this.top,
        left: event.clientX - this.left,
      };
    }),mergeMap(
      imageOffset => this.mousemove.pipe(map(pos => ({
        top: pos.clientY - imageOffset.top,
        left: pos.clientX - imageOffset.left
      })),takeUntil(this.mouseup),)),);
  }

  ngOnInit() {
    this.mousedrag.subscribe({
      next: pos => {
          this.top = pos.top;
          this.left = pos.left;
      }
    });
  }

  ngAfterViewInit(): void {
    this.analyticsSvc.postAMAEvent('TOOLTIP', this.tooltipTitle, '');
    this.show();
    this.cdr.detectChanges();
    setTimeout(_ => this.tooltipcontent.nativeElement.focus());
  }

  private resetUnderHostMode() {
    this.underHostMode = {
      active: false, 
      arrowLeftPosition: 3
    };
  }

  show() {
    this.resetUnderHostMode();
    if (this.tooltipRef) {
      const p = this.positionElements(this.tooltipRef.nativeElement.querySelector('img'), this.element.nativeElement.children[0]);
      this.updateArrowPosition();
      this.top = p.top;
      this.left = p.left;
    }
  }

  onClose(event: Event ) {
    event.preventDefault();
    event.stopPropagation();

    this.tooltipSvc.destroy();
  }

  openHelp() {
    window.open(this.helpLink);
  }

  /*
   - The default way to position the tooltip (aka target) is on the right side of the host.
   - Depending on the position of the host, the width of the tooltip, and the width of the app, it could be that
      the tooltip is not fully visible within the app's boundaries.
   - If such a case occurs, determine a position for the target/tooltip that's below the host.
   - Start with the left edge of the tooltip aligned with the left edge of the host.
   - Then shift the tooltip to the left as needed to make it fully visible within the app's boundaries.
   - Take care not to exceed the bounds of the app on the left (unlikely unless the tooltip is extremely wide).
   - If such an edge case were to occur, the user can always click and drag the tooltip to see any parts that are still out of view.
   */
  private positionElements(hostEl: HTMLElement, targetEl: HTMLElement): { top: number, left: number } {
    const hostElPos = this.position(hostEl);
    const hostBoundingRect = hostEl.getBoundingClientRect();
    let leftOffset = hostBoundingRect.left - hostElPos.left;
    const targetElWidth = targetEl.offsetWidth;

    const maxViewableWidth = window.innerWidth - 15; // 15px padding from right edge of app in case there are scroll bars
  
    // Case 1: Default positioning - target to the right of host with 5px gap
    // Target's top-left corner half the host's height above host's center point
    const defaultPosition = {
      top: hostElPos.top - (hostElPos.height / 2),
      left: hostElPos.left + hostElPos.width + 5
    };
    
    // Check if target would exceed the app's max width
    const targetRightEdgeInWindow = defaultPosition.left + targetElWidth + leftOffset;
    const exceedsMaxWidth = targetRightEdgeInWindow > maxViewableWidth;
    
    if (!exceedsMaxWidth) {
      // Case 1: Default position works
      return defaultPosition;

    } else {
      // Case 2: Target exceeds app width when placed to the right of host, so position the target under the host
      this.underHostMode.active = true;
      //The starting position under the host will be 5px below the bottom edge of host, and aligned on the left
      let case2Position = {
        top: hostElPos.top + hostElPos.height + 5,
        left: hostElPos.left
      };
      
      //Align the arrow so it is centered with the host
      //The -2px is because when the left position is 0px, it takes a shift of 2px to the right to align the 
      //bottom left corner of the arrow with the left edge of the target container
      this.underHostMode.arrowLeftPosition = (hostElPos.width / 2) - 2;

      //Does Case 2 starting position still exceed max viewable width?
      const case2RightEdgeInWindow = case2Position.left + targetElWidth + leftOffset;
      const case2ExceedsMaxWidth = case2RightEdgeInWindow > maxViewableWidth;

      if (case2ExceedsMaxWidth) {
        //From the Case 2 starting position, shift the target to the left as much as needed
        //Don't go too far to the left so that the target goes out of view on that side
        const amountExceeded = case2RightEdgeInWindow - maxViewableWidth;
        const maxShiftLeft = case2Position.left + leftOffset - 10; //always leave a 10px gap from left edge of app

        const amountToShift = Math.min(amountExceeded, maxShiftLeft);
        case2Position.left = case2Position.left - amountToShift;
        this.underHostMode.arrowLeftPosition = this.underHostMode.arrowLeftPosition + amountToShift;
      }

      return case2Position;
    }
  }
  
  // Method to update the arrow position when the tooltip is repositioned under the host
  // Set the CSS variable --arrow-left-position on the tooltip element
  private updateArrowPosition() {
    if(this.underHostMode.active){
      const tooltipElement = this.element.nativeElement.querySelector('.app-tooltip');
      if (tooltipElement) {
        tooltipElement.style.setProperty('--arrow-left-position', `${this.underHostMode.arrowLeftPosition}px`);
      }
    }
  }

  private position(nativeEl: HTMLElement): { width: number, height: number, top: number, left: number } {
    let offsetParentBCR = { top: 0, left: 0 };
    const elBCR = this.offset(nativeEl);
    const offsetParentEl = this.parentOffsetEl(nativeEl);
    if (offsetParentEl !== window.document) {
        offsetParentBCR = this.offset(offsetParentEl);
        offsetParentBCR.top += offsetParentEl.clientTop - offsetParentEl.scrollTop;
        offsetParentBCR.left += offsetParentEl.clientLeft - offsetParentEl.scrollLeft;
    }

    const boundingClientRect = nativeEl.getBoundingClientRect();
    return {
        width: boundingClientRect.width || nativeEl.offsetWidth,
        height: boundingClientRect.height || nativeEl.offsetHeight,
        top: elBCR.top - offsetParentBCR.top,
        left: elBCR.left - offsetParentBCR.left
    };
  }

  private offset(nativeEl: any): { width: number, height: number, top: number, left: number } {
    const boundingClientRect = nativeEl.getBoundingClientRect();
    return {
        width: boundingClientRect.width || nativeEl.offsetWidth,
        height: boundingClientRect.height || nativeEl.offsetHeight,
        top: boundingClientRect.top + (window.pageYOffset || window.document.documentElement.scrollTop),
        left: boundingClientRect.left + (window.pageXOffset || window.document.documentElement.scrollLeft)
    };
  }

  private parentOffsetEl(nativeEl: HTMLElement): any {
    let offsetParent: any = nativeEl.offsetParent || window.document;
    while (offsetParent && offsetParent !== window.document && this.isStaticPositioned(offsetParent)) {
        offsetParent = offsetParent.offsetParent;
    }
    return offsetParent || window.document;
  }

  private isStaticPositioned(nativeEl: HTMLElement): boolean {
    return (this.getStyle(nativeEl, 'position') || 'static' ) === 'static';
  }

 private getStyle(nativeEl: HTMLElement, cssProp: string): string {
    if ((nativeEl as any).currentStyle) { // IE
        return (nativeEl as any).currentStyle[cssProp];
    }

    if (window.getComputedStyle) {
        return (window.getComputedStyle(nativeEl) as any)[cssProp];
    }

    // finally try and get inline style
    return (nativeEl.style as any)[cssProp];
  }
}


